import { useState } from 'react';
import {
  Button,
  Card,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { useRevokeSession, useSessions } from './useIam';

/* ── Helpers ── */

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR');
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

function timeSince(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Agora';
  if (minutes < 60) return `${minutes}min atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

/* ── Component ── */

export function SessionsPage(): JSX.Element {
  const { addToast } = useToast();
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const { data, isLoading, error } = useSessions();

  const revokeMutation = useRevokeSession();

  const handleRevoke = (sessionId: string): void => {
    revokeMutation.mutate(sessionId, {
      onSuccess: () => {
        addToast('Sessão revogada com sucesso', 'success');
        setConfirmRevokeId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao revogar sessão', 'error');
        setConfirmRevokeId(null);
      },
    });
  };

  return (
    <div className="iam-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Sessões</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Sessões ativas e dispositivos conectados.</p>
        </div>
      </div>

      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar sessões: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhuma sessão ativa encontrada.</p>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>IP</th>
                  <th>User-Agent</th>
                  <th>Última Atividade</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((session) => (
                  <tr key={session.id}>
                    <td>{session.userName}</td>
                    <td><code className="ip-code">{session.ipAddress}</code></td>
                    <td title={session.userAgent}>{truncate(session.userAgent, 50)}</td>
                    <td>
                      <span className="last-activity">{timeSince(session.lastActivity)}</span>
                      <span className="last-activity-date">{formatDateTime(session.lastActivity)}</span>
                    </td>
                    <td>
                      {confirmRevokeId === session.id ? (
                        <div className="inline-confirm">
                          <span className="confirm-text">Revogar?</span>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={revokeMutation.isPending}
                            onClick={() => handleRevoke(session.id)}
                          >
                            Sim
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmRevokeId(null)}
                            disabled={revokeMutation.isPending}
                          >
                            Não
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmRevokeId(session.id)}
                        >
                          Revogar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .iam-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; }
        .empty-message { color: #74777f; font-size: 0.875rem; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .ip-code { font-size: 0.8125rem; background: #f2f4f6; padding: 0.125rem 0.375rem; border-radius: 4px; }
        .last-activity { font-weight: 600; color: #002045; margin-right: 0.5rem; }
        .last-activity-date { font-size: 0.75rem; color: #74777f; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; margin-right: 0.25rem; }
      `}</style>
    </div>
  );
}
